import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { BASE_URL } from "../../config";
import { Plus, Trash2, UploadCloud } from "lucide-react";
import "./material.css";

export default function AddMaterial() {
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const user = JSON.parse(localStorage.getItem("NagpurProperties") || "{}");

  const canViewPrice =
    user?.role?.some(
      (r) =>
        r.roleName === "Admin" ||
        r.roleName === "SuperAdmin" ||
        r.roleName === "SuperEngineer"
    ) || false;

  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [vehicalImg, setVehicalImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    vendorId: vendorId || "",
    siteId: "",
    vehicleNo: "",
    remark: "",
    engineerName: user?.email || "",
  });

  const [materialItems, setMaterialItems] = useState([
    {
      materialId: "",
      dailyMaterialName: "",
      unit: "0",
      size: "0",
      materialQty: "0",
      price: "0",
      total: "",
    },
  ]);

  useEffect(() => {
    const load = async () => {
      const [v, s, m] = await Promise.all([
        axiosInstance.get("/showAllvendor"),
        axiosInstance.get("/show-AllProject"),
        axiosInstance.get("/materials"),
      ]);
      setVendors(v.data || []);
      setSites(s.data || []);
      setMaterials(m.data || []);
    };
    load();
  }, []);

  const updateItem = (i, field, value) => {
    const copy = [...materialItems];
    copy[i][field] = value;

    if (field === "materialId") {
      const found = materials.find((m) => String(m.id) === String(value));
      copy[i].dailyMaterialName = found?.materialName || "";
    }

    if (canViewPrice && (field === "materialQty" || field === "price")) {
      copy[i].total = (
        Number(copy[i].materialQty || 0) * Number(copy[i].price || 0)
      ).toFixed(2);
    }

    setMaterialItems(copy);
  };

  const selectedVendor = vendors.find((v) => v.id === Number(form.vendorId));
  const selectedSite = sites.find((s) => s.id === Number(form.siteId));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      vendorId: Number(form.vendorId),
      siteId: Number(form.siteId),
      supplierName: selectedVendor?.vendorName || null,
      siteName: selectedSite?.name || null,
      materialItems: materialItems.map((i) => ({
        materialId: Number(i.materialId),
        dailyMaterialName: i.dailyMaterialName || "",
        unit: i.unit,
        size: i.size,
        materialQty: Number(i.materialQty),
        price: canViewPrice ? Number(i.price || 0) : 0,
        total: canViewPrice ? Number(i.total || 0) : 0,
      })),
    };

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append(
        "data",
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
      if (vehicalImg) fd.append("vehicalImg", vehicalImg);

      await axiosInstance.post(`${BASE_URL}/DailyMaterial/add`, fd, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      navigate(`/material/${vendorId}`);
    } catch {
      setError("Failed to save. Check the details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const grandTotal = materialItems
    .reduce((sum, i) => sum + Number(i.total || 0), 0)
    .toFixed(2);

  return (
    <div className="am-page">
      <div className="am-card">
        <header className="am-header">
          <span className="am-eyebrow">Vendor #{form.vendorId}</span>
          <h1>Log daily material</h1>
          <p>Record what arrived on site today, by vehicle and quantity.</p>
        </header>

        {error && <div className="am-alert" role="alert">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <section className="am-section">
            <h2 className="am-section-title">Delivery details</h2>

            <div className="am-grid">
              <div className="am-field">
                <label htmlFor="vendor">Vendor</label>
                <select id="vendor" value={form.vendorId} disabled>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.vendorName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="am-field">
                <label htmlFor="site">Site *</label>
                <select
                  id="site"
                  value={form.siteId}
                  onChange={(e) =>
                    setForm({ ...form, siteId: e.target.value })
                  }
                  required
                >
                  <option value="">Select site</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="am-field">
                <label htmlFor="vehicleNo">Vehicle number *</label>
                <input
                  id="vehicleNo"
                  placeholder="e.g. MH31 AB 1234"
                  value={form.vehicleNo}
                  onChange={(e) =>
                    setForm({ ...form, vehicleNo: e.target.value })
                  }
                  required
                />
              </div>

              <div className="am-field">
                <label htmlFor="vehicleImg">Vehicle photo</label>
                <label className="am-file" htmlFor="vehicleImg">
                  <UploadCloud size={16} aria-hidden="true" />
                  <span>{vehicalImg ? vehicalImg.name : "Upload photo"}</span>
                </label>
                <input
                  id="vehicleImg"
                  type="file"
                  accept="image/*"
                  className="am-file-input"
                  onChange={(e) => setVehicalImg(e.target.files[0])}
                />
              </div>
            </div>

            <div className="am-field">
              <label htmlFor="remark">Remark</label>
              <textarea
                id="remark"
                rows="3"
                placeholder="Anything the site team should know about this delivery"
                value={form.remark}
                onChange={(e) => setForm({ ...form, remark: e.target.value })}
              />
            </div>
          </section>

          <section className="am-section">
            <div className="am-section-head">
              <h2 className="am-section-title">Materials</h2>
              {canViewPrice && (
                <span className="am-total-pill">
                  Total&nbsp;<strong>₹{grandTotal}</strong>
                </span>
              )}
            </div>

            <div className="am-items">
              {materialItems.map((item, i) => (
                <div className="am-item" key={i}>
                  <div className="am-item-grid">
                    <div className="am-field">
                      <label htmlFor={`material-${i}`}>Material</label>
                      <select
                        id={`material-${i}`}
                        value={item.materialId}
                        onChange={(e) =>
                          updateItem(i, "materialId", e.target.value)
                        }
                        required
                      >
                        <option value="">Select material</option>
                        {materials.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.materialName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="am-field">
                      <label htmlFor={`unit-${i}`}>Unit</label>
                      <input
                        id={`unit-${i}`}
                        value={item.unit}
                        onChange={(e) =>
                          updateItem(i, "unit", e.target.value)
                        }
                        required
                      />
                    </div>

                    <div className="am-field">
                      <label htmlFor={`size-${i}`}>Size</label>
                      <input
                        id={`size-${i}`}
                        value={item.size}
                        onChange={(e) =>
                          updateItem(i, "size", e.target.value)
                        }
                      />
                    </div>

                    <div className="am-field">
                      <label htmlFor={`qty-${i}`}>Quantity</label>
                      <input
                        id={`qty-${i}`}
                        type="number"
                        value={item.materialQty}
                        onChange={(e) =>
                          updateItem(i, "materialQty", e.target.value)
                        }
                        required
                      />
                    </div>

                    {canViewPrice && (
                      <div className="am-field">
                        <label htmlFor={`price-${i}`}>Price</label>
                        <input
                          id={`price-${i}`}
                          type="number"
                          value={item.price}
                          onChange={(e) =>
                            updateItem(i, "price", e.target.value)
                          }
                        />
                      </div>
                    )}

                    {canViewPrice && (
                      <div className="am-field am-field-static">
                        <span className="am-static-label">Total</span>
                        <span className="am-static-value">
                          ₹{item.total || 0}
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="am-remove"
                    onClick={() =>
                      setMaterialItems(
                        materialItems.filter((_, x) => x !== i)
                      )
                    }
                    disabled={materialItems.length === 1}
                    aria-label="Remove this material"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    <span>Remove</span>
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="am-add-row"
              onClick={() =>
                setMaterialItems([
                  ...materialItems,
                  {
                    materialId: "",
                    dailyMaterialName: "",
                    unit: "",
                    size: "",
                    materialQty: "",
                    price: "",
                    total: "",
                  },
                ])
              }
            >
              <Plus size={16} aria-hidden="true" /> Add another material
            </button>
          </section>

          <div className="am-footer">
            <button type="submit" className="am-submit" disabled={loading}>
              {loading ? "Saving…" : "Save entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}